import { useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Card, Select, Button, Checkbox, Alert, Collapse, Typography, Space, Empty,
  Row, Col, App as AntApp, Flex,
} from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import { API_URL } from '../config';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

type Metrica = {
  key: string;
  label: string;
  grupo: 'Receitas' | 'Resultados' | 'Despesas' | 'Lucro';
  color: string;
  get: (d: any) => number;
};

const METRICAS: Metrica[] = [
  { key: 'recBruta', label: 'Receita Bruta Total', grupo: 'Receitas', color: '#0930D0', get: d => d?.receitas?.recBruta ?? 0 },
  { key: 'recLiqComercial', label: 'Receita Líquida Comercial', grupo: 'Receitas', color: '#02B5DB', get: d => d?.receitas?.recLiqComercial ?? 0 },
  { key: 'recFin', label: 'Receitas Financeiras', grupo: 'Receitas', color: '#7BE0FF', get: d => d?.financeiro?.recFin ?? 0 },
  { key: 'lucroBruto', label: 'Lucro Bruto', grupo: 'Lucro', color: '#020D7F', get: d => d?.lucroBruto ?? 0 },
  { key: 'lucroLiq', label: 'Lucro Líquido', grupo: 'Lucro', color: '#0930D0', get: d => d?.lucroLiq ?? 0 },
  { key: 'lucroFinal', label: 'Lucro Final', grupo: 'Lucro', color: '#02B5DB', get: d => d?.lucroFinal ?? 0 },
  { key: 'resultadoOp', label: 'Resultado Operacional', grupo: 'Resultados', color: '#6366F1', get: d => d?.resultadoOp ?? 0 },
  { key: 'resultadoAntes', label: 'Resultado Antes dos Tributos', grupo: 'Resultados', color: '#8B5CF6', get: d => d?.resultadoAntes ?? 0 },
  { key: 'comissoes', label: 'Comissões de Vendas', grupo: 'Despesas', color: '#D97706', get: d => d?.receitas?.comissoes ?? 0 },
  { key: 'custosOp', label: 'Custos Operacionais', grupo: 'Despesas', color: '#B91626', get: d => d?.custos?.custosOp ?? 0 },
  { key: 'despComercial', label: 'Despesas Comerciais', grupo: 'Despesas', color: '#E24B4A', get: d => d?.despesas?.despComercial ?? 0 },
  { key: 'despAdmin', label: 'Despesas Administrativas', grupo: 'Despesas', color: '#F59E0B', get: d => d?.despesas?.despAdmin ?? 0 },
  { key: 'outrasDespOp', label: 'Outras Despesas Op.', grupo: 'Despesas', color: '#EA580C', get: d => d?.despesas?.outrasDespOp ?? 0 },
  { key: 'despFin', label: 'Despesas Financeiras', grupo: 'Despesas', color: '#DC2626', get: d => d?.financeiro?.despFin ?? 0 },
  { key: 'impostos', label: 'Impostos', grupo: 'Despesas', color: '#7C2D12', get: d => d?.impostos?.total ?? 0 },
  { key: 'investimentos', label: 'Investimentos', grupo: 'Despesas', color: '#A16207', get: d => d?.investimentos?.total ?? 0 },
];

function listarTrimestres(inicio: string, fim: string): string[] {
  const [ai, qi] = inicio.split('/');
  const [af, qf] = fim.split('/');
  const yi = parseInt(ai, 10), yf = parseInt(af, 10);
  const ni = parseInt(qi.substring(1), 10), nf = parseInt(qf.substring(1), 10);
  const startIdx = yi * 4 + (ni - 1);
  const endIdx = yf * 4 + (nf - 1);
  if (endIdx < startIdx) return [];
  const out: string[] = [];
  for (let i = startIdx; i <= endIdx; i++) {
    const y = Math.floor(i / 4);
    const q = (i % 4) + 1;
    out.push(`${y}/Q${q}`);
  }
  return out;
}

const fmtBR = (v: number) => 'R$ ' + (v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

function HistoricoDREInner() {
  const anoAtual = new Date().getFullYear();
  const anos = [anoAtual - 2, anoAtual - 1, anoAtual];
  const [inicio, setInicio] = useState(`${anoAtual - 1}/Q1`);
  const [fim, setFim] = useState(`${anoAtual}/Q4`);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(
    new Set(['recBruta', 'lucroBruto', 'lucroLiq'])
  );
  const [serie, setSerie] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  function toggleMetrica(key: string) {
    setSelecionadas(prev => {
      const novo = new Set(prev);
      if (novo.has(key)) novo.delete(key); else novo.add(key);
      return novo;
    });
  }

  async function gerar() {
    setErro('');
    const trims = listarTrimestres(inicio, fim);
    if (trims.length === 0) { setErro('Período inválido: o fim deve ser após o início.'); return; }
    if (selecionadas.size === 0) { setErro('Selecione ao menos uma métrica.'); return; }
    setLoading(true);
    try {
      const token = await getToken();
      const resps = await Promise.all(trims.map(t =>
        axios.get(`${API_URL}/dre/trimestre/${encodeURIComponent(t)}`, {
          headers: { Authorization: token },
        })
      ));
      const dados = resps.map((r, i) => {
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        const row: any = { periodo: trims[i].replace('/', ' ') };
        METRICAS.forEach(m => {
          if (selecionadas.has(m.key)) row[m.key] = m.get(d);
        });
        return row;
      });
      setSerie(dados);
    } catch (e: any) {
      setErro('Erro ao carregar série: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  const opcoesTrim: { label: string; value: string }[] = [];
  anos.forEach(y => ['Q1', 'Q2', 'Q3', 'Q4'].forEach(q => {
    const v = `${y}/${q}`;
    opcoesTrim.push({ label: v.replace('/', ' '), value: v });
  }));

  return (
    <Card
      size="small"
      title={
        <Flex align="center" gap={6}>
          <LineChartOutlined style={{ color: '#020D7F' }} />
          <Text strong style={{ color: '#020D7F', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Evolução Histórica
          </Text>
        </Flex>
      }
    >
      <Flex gap={8} align="center" wrap="wrap" style={{ marginBottom: 12 }}>
        <Space size={4}>
          <Text style={{ fontSize: 11, color: '#555' }}>De</Text>
          <Select
            value={inicio}
            onChange={setInicio}
            options={opcoesTrim}
            size="small"
            style={{ width: 100 }}
          />
        </Space>
        <Space size={4}>
          <Text style={{ fontSize: 11, color: '#555' }}>Até</Text>
          <Select
            value={fim}
            onChange={setFim}
            options={opcoesTrim}
            size="small"
            style={{ width: 100 }}
          />
        </Space>
        <Button type="primary" size="small" loading={loading} onClick={gerar}>Gerar</Button>
      </Flex>

      <Collapse
        size="small"
        ghost
        items={[{
          key: 'm',
          label: (
            <Text style={{ fontSize: 11, fontWeight: 600, color: '#6A7092', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Métricas ({selecionadas.size} selecionadas)
            </Text>
          ),
          children: (
            <>
              <Row gutter={20} style={{ marginBottom: 8 }}>
                {(['Receitas', 'Lucro', 'Resultados'] as Metrica['grupo'][]).map(grupo => (
                  <Col key={grupo} xs={24} sm={8}>
                    <Text style={{ fontSize: 9, fontWeight: 700, color: '#020D7F', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 3 }}>
                      {grupo}
                    </Text>
                    <Space direction="vertical" size={2}>
                      {METRICAS.filter(m => m.grupo === grupo).map(m => (
                        <Checkbox
                          key={m.key}
                          checked={selecionadas.has(m.key)}
                          onChange={() => toggleMetrica(m.key)}
                        >
                          <Space size={4}>
                            <span style={{ width: 8, height: 8, borderRadius: 2, background: m.color, display: 'inline-block' }} />
                            <Text style={{ fontSize: 11 }}>{m.label}</Text>
                          </Space>
                        </Checkbox>
                      ))}
                    </Space>
                  </Col>
                ))}
              </Row>
              <div>
                <Text style={{ fontSize: 9, fontWeight: 700, color: '#020D7F', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 3 }}>
                  Despesas
                </Text>
                <Row gutter={[12, 2]}>
                  {METRICAS.filter(m => m.grupo === 'Despesas').map(m => (
                    <Col key={m.key} xs={12} sm={8} md={6}>
                      <Checkbox
                        checked={selecionadas.has(m.key)}
                        onChange={() => toggleMetrica(m.key)}
                      >
                        <Space size={4}>
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: m.color, display: 'inline-block' }} />
                          <Text style={{ fontSize: 11 }}>{m.label}</Text>
                        </Space>
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          ),
        }]}
        style={{ marginBottom: 12 }}
      />

      {erro && <Alert type="error" message={erro} showIcon style={{ marginBottom: 12 }} closable onClose={() => setErro('')} />}

      {serie.length > 0 ? (
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={serie} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="periodo" tick={{ fontSize: 11, fill: '#555' }} />
              <YAxis
                tick={{ fontSize: 11, fill: '#555' }}
                tickFormatter={(v: any) => {
                  const n = typeof v === 'number' ? v : parseFloat(v) || 0;
                  const abs = Math.abs(n);
                  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
                  if (abs >= 1_000) return (n / 1_000).toFixed(0) + 'k';
                  return String(n);
                }}
              />
              <Tooltip
                formatter={(v: any) => fmtBR(typeof v === 'number' ? v : parseFloat(v) || 0)}
                contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e0e0e0' }}
                labelStyle={{ fontWeight: 600, color: '#020D7F' }}
              />
              <Legend wrapperStyle={{ fontSize: 10, lineHeight: '16px' }} />
              {METRICAS.filter(m => selecionadas.has(m.key)).map(m => (
                <Line
                  key={m.key} type="monotone" dataKey={m.key} name={m.label}
                  stroke={m.color} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (!loading && !erro && (
        <Empty
          description="Selecione o período e as métricas, depois clique em Gerar."
          imageStyle={{ height: 60 }}
          style={{ padding: '24px 0' }}
        />
      ))}
    </Card>
  );
}

export default function HistoricoDRE() {
  return (
    <AntApp>
      <HistoricoDREInner />
    </AntApp>
  );
}
